/**
 * AuditItemContent Component
 *
 * Handles the content summary display including transcription text and metadata.
 * Extracted from AuditItem.tsx to reduce complexity and improve maintainability.
 */

import { InlineAudioPlayer, ImageViewer } from '@core/components';
import { useSystemConfig } from '@core/hooks/useSystemConfig';
import type { GraphQLAuditItem, GraphQLAuditDecision } from '@internal-audit/domain/graphql-types';
import { BlurToggle, contentBlurStyles, Text } from '@soteria/design-system-ve';
import { useState } from 'react';

import * as styles from '../AuditItem.css';
import * as imageStyles from '../AuditItemImage.css';
import { useAuditItemMetadata } from '../hooks/useAuditItemMetadata';

import { BasicMetadataSection } from './BasicMetadataSection';
import { ModelVersionsSection } from './ModelVersionsSection';
import { PolicyBadgesSection } from './PolicyBadgesSection';

interface AuditItemContentProps {
  item: GraphQLAuditItem;
  decisions?: GraphQLAuditDecision[];
  isContentBlurred?: boolean;
  onToggleBlur?: () => void;
}

// Helper function to get transcription text with fallback
const getTranscriptionText = (item: GraphQLAuditItem): string => item.contentSnapshot?.transcription || 'No transcription available';

// Helper function to get border class based on content safety
const getBorderClass = (isSafeContent: boolean): string => (isSafeContent ? styles.safeContentBorder : styles.unsafeContentBorder);

/**
 * Image thumbnail component
 */
function ImageThumbnail({
  imageUrl,
  altText,
  isSafe,
  isContentBlurred,
  onClick,
}: {
  imageUrl: string;
  altText: string;
  isSafe: boolean;
  isContentBlurred: boolean;
  onClick: () => void;
}) {
  const [imageError, setImageError] = useState(false);
  const applyBlur = !isSafe && isContentBlurred;

  return (
    <button aria-label="View full-size image" className={imageStyles.thumbnailContainer} type="button" onClick={onClick}>
      {imageError ? (
        <Text className={imageStyles.thumbnailErrorText}>Image unavailable</Text>
      ) : (
        <img
          alt={altText}
          className={`${imageStyles.thumbnailImage} ${applyBlur ? contentBlurStyles.blurredThumbnail : ''}`}
          src={imageUrl}
          onError={() => setImageError(true)}
        />
      )}
    </button>
  );
}

/**
 * Audio player control component
 */
function AudioPlayerControl({ item, contentUrl, mediaServiceUrl }: { item: GraphQLAuditItem; contentUrl: string; mediaServiceUrl?: string }) {
  return (
    <div className={styles.audioPlayerSection}>
      <InlineAudioPlayer audioId={item.id} audioUrl={contentUrl} mediaServiceUrl={mediaServiceUrl} transcription={item.contentSnapshot?.transcription} />
    </div>
  );
}

/**
 * Metadata controls section
 */
function MetadataControls({
  item,
  shouldShowAudioPlayer,
  contentUrl,
  mediaServiceUrl,
  isSafeContent,
  isContentBlurred,
  onToggleBlur,
}: {
  item: GraphQLAuditItem;
  shouldShowAudioPlayer: boolean;
  contentUrl?: string;
  mediaServiceUrl?: string;
  isSafeContent: boolean;
  isContentBlurred: boolean;
  onToggleBlur?: () => void;
}) {
  const showAudio = shouldShowAudioPlayer && contentUrl;
  const showBlurToggle = !isSafeContent && onToggleBlur;
  const showControls = showAudio || showBlurToggle;

  const handleBlurToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleBlur?.();
  };

  if (!showControls) {
    return null;
  }

  return (
    <div className={imageStyles.metadataRightSection}>
      {showAudio && <AudioPlayerControl contentUrl={contentUrl} item={item} mediaServiceUrl={mediaServiceUrl} />}
      {showBlurToggle && <BlurToggle isBlurred={isContentBlurred} onToggle={handleBlurToggle} />}
    </div>
  );
}

/**
 * Content summary section with image and metadata
 */
function ContentSummarySection({
  item,
  hasImage,
  contentUrl,
  isSafeContent,
  isContentBlurred,
  contentType,
  topic,
  mediaServiceUrl,
  shouldShowAudioPlayer,
  contentSummaryClass,
  textClassName,
  transcriptionText,
  onImageClick,
  onToggleBlur,
}: {
  item: GraphQLAuditItem;
  hasImage: boolean;
  contentUrl: string | undefined;
  isSafeContent: boolean;
  isContentBlurred: boolean;
  contentType: string | undefined;
  topic: string | undefined;
  mediaServiceUrl: string | undefined;
  shouldShowAudioPlayer: boolean;
  contentSummaryClass: string;
  textClassName: string;
  transcriptionText: string;
  onImageClick: () => void;
  onToggleBlur: (() => void) | undefined;
}) {
  return (
    <div className={contentSummaryClass}>
      {hasImage && (
        <ImageThumbnail
          altText={`Audit item ${item.id} image`}
          imageUrl={contentUrl || ''}
          isContentBlurred={isContentBlurred}
          isSafe={isSafeContent}
          onClick={onImageClick}
        />
      )}
      <div className={hasImage ? imageStyles.contentColumn : ''}>
        <div className={styles.transcriptionContent}>
          <p className={textClassName}>{transcriptionText}</p>
        </div>
        <div className={styles.metadataRow}>
          <div className={styles.metadataLeftSection}>
            <BasicMetadataSection contentType={contentType} item={item} topic={topic} />
            <PolicyBadgesSection item={item} />
          </div>
          <MetadataControls
            contentUrl={contentUrl}
            isContentBlurred={isContentBlurred}
            isSafeContent={isSafeContent}
            item={item}
            mediaServiceUrl={mediaServiceUrl}
            shouldShowAudioPlayer={shouldShowAudioPlayer}
            onToggleBlur={onToggleBlur}
          />
        </div>
        <ModelVersionsSection item={item} />
      </div>
    </div>
  );
}

export function AuditItemContent({ item, isContentBlurred = true, onToggleBlur }: AuditItemContentProps) {
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const { mediaServiceUrl } = useSystemConfig();
  const { isSafeContent, topic, contentType, contentUrl, shouldShowAudioPlayer, shouldShowImageViewer } = useAuditItemMetadata(item);

  const handleImageClick = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setIsImageViewerOpen(true);
  };

  const transcriptionText = getTranscriptionText(item);
  const borderClass = getBorderClass(isSafeContent);
  const hasImage = Boolean(shouldShowImageViewer && contentUrl);
  const contentSummaryClass = `${styles.contentSummary} ${hasImage ? imageStyles.contentSummaryWithImage : ''} ${borderClass}`;
  const textClassName = `${styles.transcriptionText} ${!isSafeContent && isContentBlurred ? contentBlurStyles.blurredText : ''}`;
  const showImageViewer = Boolean(shouldShowImageViewer && contentUrl);

  return (
    <>
      <ContentSummarySection
        contentSummaryClass={contentSummaryClass}
        contentType={contentType}
        contentUrl={contentUrl}
        hasImage={hasImage}
        isContentBlurred={isContentBlurred}
        isSafeContent={isSafeContent}
        item={item}
        mediaServiceUrl={mediaServiceUrl}
        shouldShowAudioPlayer={Boolean(shouldShowAudioPlayer)}
        textClassName={textClassName}
        topic={topic}
        transcriptionText={transcriptionText}
        onImageClick={handleImageClick}
        onToggleBlur={onToggleBlur}
      />
      {showImageViewer && contentUrl && (
        <ImageViewer
          altText={`Audit item ${item.id} image`}
          imageUrl={contentUrl}
          isBlurred={isContentBlurred}
          isOpen={isImageViewerOpen}
          isSafe={isSafeContent}
          title="Audit Content Image"
          onClose={() => setIsImageViewerOpen(false)}
          onToggleBlur={onToggleBlur}
        />
      )}
    </>
  );
}
